import { memo } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { PersonQuestion } from '../Icons'
import { MentorLoader } from './MentorLoader'

interface ICardLayoutProps {
  title: string
  imageUrl: string
  mentor?: {
    success: boolean
    mentorId: string | null
    gender: string | null
    firstName: string | null
    company: string | null
    headline: string | null
    industry: string | null
    jobTitle: string | null
    lastName: string | null
    location: string
    previewUrl: string | null
    videoLoopUrl: string | null
    department: string | null
    race: string | null
    mentorType: string | null
    userContextId: string | null
    slug: string
  } | null
  id: string
  handleCardClick: (e: React.MouseEvent<HTMLAnchorElement>) => void
  handleCopyLink: (e: React.MouseEvent<HTMLButtonElement>) => void
  timeAgo?: string
  isMentorGenerating?: boolean
}

const CardLayout: React.FC<ICardLayoutProps> = memo(
  ({
    mentor,
    id,
    handleCardClick,
    handleCopyLink,
    title,
    imageUrl,
    timeAgo,
    isMentorGenerating,
  }) => {
    const navigate = useNavigate()
    const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
      e.stopPropagation()
      e.preventDefault()
      if (mentor?.slug) {
        navigate(`/${mentor.slug}?start=true&context=${id}`, {
          state: {
            prevLocation: `${window.location.pathname}${window.location.search || ''}`,
          },
        })
        handleCardClick(e as unknown as React.MouseEvent<HTMLAnchorElement>)
      }
    }

    const stopPropagationClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.stopPropagation()
    }

    return (
      <div className="tc-mv-card-item tc-mv-card-title ">
        {isMentorGenerating ? (
          <div className="text-white bg-[#8080804d] absolute px-4 py-2 rounded-full text-xs top-[35%] left-1/2 -translate-x-1/2 z-10 border-[1px] border-[#ffffff66]">
            {imageUrl ? 'Generating companion...' : 'Generating...'}
          </div>
        ) : (
          <button
            className="absolute top-2 right-2 z-10 h-10 w-10 min-w-10 flex items-center justify-center text-white font-medium tc-mv-blur align-middle rounded-full border-0 copy-title-link"
            onClick={handleCopyLink}
            data-testid="copy-title-button"
          >
            <i className="fa-solid fa-share" />
          </button>
        )}
        <Link
          to={`/${mentor?.slug}?start=true&context=${id}`}
          state={{
            prevLocation: `${window.location.pathname}${window.location.search}`,
          }}
          className={`${!mentor?.slug ? 'pointer-events-none' : ''}`}
          onClick={handleCardClick}
        >
          <div className="tc-mv-card-bg-img ">
            {imageUrl && (
              <img
                src={imageUrl}
                alt={title}
                className="object-cover w-full h-full"
              />
            )}
          </div>
        </Link>

        <div
          onClick={handleClick}
          className="tc-mv-avatar-details flex content-center items-center cursor-pointer"
        >
          <div
            className={`relative flex items-center justify-center text-dark-neutral-800 w-[72px] h-[72px] min-w-[72px] rounded-full overflow-hidden mr-4 bg-dark-neutral-400  border-0 ${
              mentor?.previewUrl || mentor?.videoLoopUrl ? '' : 'animate-pulse'
            }`}
          >
            {mentor?.previewUrl || mentor?.videoLoopUrl ? (
              <Link
                to={`/${mentor?.slug}?start=true`}
                state={{
                  prevLocation: `${window.location.pathname}${window.location.search}`,
                }}
                className={`w-full h-full ${!mentor?.slug ? 'pointer-events-none' : ''}`}
                onClick={stopPropagationClick}
              >
                <video
                  preload="metadata"
                  src={mentor?.videoLoopUrl || undefined}
                  muted
                  autoPlay
                  loop
                  playsInline
                  controlsList="nodownload"
                  poster={mentor?.previewUrl || undefined}
                  disablePictureInPicture
                  className="w-full h-full object-cover"
                />
              </Link>
            ) : isMentorGenerating ? (
              <MentorLoader />
            ) : (
              <PersonQuestion />
            )}
          </div>
          <div>
            {timeAgo && (
              <div className="text-[#F2F2F4] text-10px">{timeAgo}</div>
            )}
            <div className="tc-mv-avatar-name line-clamp-2">
              {title || (
                <div className="w-64 h-[24px] bg-dark-neutral-400 rounded-full animate-pulse border-0 !mb-2" />
              )}
            </div>
            <div className="inline-flex items-center gap-2 text-dark-neutral-400 whitespace-nowrap flex-wrap text-sm">
              {mentor?.firstName ? (
                <>
                  <Link
                    to={`/${mentor?.slug}?start=true`}
                    className="text-current hover:text-dark-neutral-200 capitalize"
                    state={{
                      prevLocation: `${window.location.pathname}${window.location.search}`,
                    }}
                    onClick={stopPropagationClick}
                  >
                    {`${mentor?.firstName} ${mentor?.lastName}`}
                  </Link>

                  <span
                    className="w-1 h-1 bg-current rounded-full"
                    aria-hidden="true"
                  />

                  <span>{mentor?.jobTitle}</span>
                </>
              ) : (
                <div className="w-48 h-[16px] bg-current rounded-full animate-pulse border-0" />
              )}
            </div>
          </div>
        </div>
      </div>
    )
  },
)

export { CardLayout }
